import { canonicalFeedbackPreviewEnabled } from '@/features/feedback-integration/feedbackPreview';
import { CanonicalRecipientFeedback } from '@/features/feedback-integration/CanonicalRecipientFeedback';
import { CanonicalAthleteFeedback } from '@/features/feedback-integration/CanonicalAthleteFeedback';
import { FeedbackConversationHost } from '@/features/feedback-integration/FeedbackConversationHost';
import { useMeAthletes } from '@/features/athletes/hooks/useAthletes';
import { CanonicalRecapHost } from '@/features/recaps/CanonicalRecapHost';
import { useRecapScope } from '@/features/recaps/useCanonicalRecap';
import { feedbackNavigationTarget, feedbackNavigationSearch } from '@/lib/feedbackNavigation';
import type { FeedbackConversationTarget, LinkedAthlete } from '@/lib/types';
import { feedbackForPlayer, selectLinkedPlayer } from "./playerContext";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useRouter, useSearch } from "@tanstack/react-router";
import { ApiError, getAppSettings } from "@/lib/api";
import { publicAppSettingsKey } from "@/lib/queryClient";
import { getUploadedClubLogoUrl } from "@/lib/matchLogos";
import { useMe } from "@/features/auth/useMe";
import { HelpLink } from "@/features/docs/HelpLink";
import { FeedbackTabStrip } from "./FeedbackTabStrip";
import { LinkedPlayersStrip } from "./components/LinkedPlayersStrip";
import { FeedbackOverviewTab } from "./FeedbackOverviewTab";
import { FeedbackReviewTab } from "./FeedbackReviewTab";
import { FeedbackHistoryTab } from "./FeedbackHistoryTab";
import { FeedbackGoalsTab } from "./FeedbackGoalsTab";
import {
  FEEDBACK_TAB_LABELS,
  type FeedbackTab,
  normalizeFeedbackTab,
} from "./feedbackTabs";
import {
  reviewedClipIds as deriveReviewedClipIds,
  reviewedNoteIds as deriveReviewedNoteIds,
  reviewedPlaylistIds as deriveReviewedPlaylistIds,
  reviewedSummaryIds as deriveReviewedSummaryIds,
} from "./feedbackHelpers";
import { useMyFeedback } from "./hooks/useMyFeedback";
import { useScopeIdentity } from "@/features/navigation/useScopeSelection";
import { GlobalScopeBar, isRecipientWorkspacePath } from "@/features/navigation/GlobalScopeBar";
import { TeamScopeGate } from "@/features/navigation/TeamScopeGate";
import { getPublicNavCapabilities } from "@/features/navigation/navCapabilities";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";
import { Inbox } from "lucide-react";
import { Button } from "@/components/ui/Button";

/**
 * Player/guardian feedback shell — the host for the `/feedback`
 * viewer surfaces.
 *
 * PR 1 of the Feedback Player UX redesign: the four action tabs
 * (Overview · Review · Goals · History) replace the legacy object
 * tabs. The router still accepts the five legacy `?tab=` values
 * (`playlists`, `notes`, `clips`, `summaries`, `development`) and the
 * shell folds them via `normalizeFeedbackTab`; no URL rewrite, so
 * back-button history stays stable.
 *
 * `useMe()` reconciles the auth + scope stores on a direct nav —
 * `/api/my-feedback` resolves the viewer's team from the request
 * scope, so the scope store must be populated first.
 *
 * `useMyFeedback()` is the single mount-time bundle fetch feeding
 * every tab. A 401 / missing token routes to `/login?next=...`.
 *
 * PR 2 ships the unified Review feed + filter chips; PR 3 reshapes
 * the Goals page (action-plan-first via `FeedbackGoalsTab`, the
 * viewer-mode wrapper around the shared development data); PR 5
 * upgrades the History wrapper into a grouped archive.
 */

export function FeedbackShell() {
  useMe();
  const identity = useScopeIdentity();
  const ready = useAuthStore(s => !s.hydrating && !!s.user);
  const [admitted, setAdmitted] = useState({ identity, ready });
  const router = useRouter();
  const navigate = useNavigate({ from: "/feedback" });
  const search = useSearch({ from: "/feedback" });
  useLayoutEffect(() => {
    if (admitted.identity === identity) return;
    // A saved context change retires all old destinations before mounting
    // another reader. Logout is an authority departure, not a scope choice;
    // a retiring shell must also leave an in-flight destination alone.
    // Keep the explicit child ID to verify against fresh links.
    if (admitted.ready && ready && isRecipientWorkspacePath(router.latestLocation.pathname)) void navigate({ search: previous => ({ ...previous, note: undefined, clip: undefined,
      summary: undefined, goal: undefined, feedback_id: undefined,
      feedback_player_id: undefined, recap_id: undefined }), replace: true });
    setAdmitted({ identity, ready });
  }, [admitted, identity, ready, navigate, router]);
  if (admitted.identity !== identity) return null;
  return <FeedbackContext key={`${identity.authority}:${identity.generation}:${search.player ?? ''}`} />;
}

function FeedbackContext() {
  // Reconcile auth + scope — `/api/my-feedback` is scope-resolved
  // server-side, so a direct nav must not leave the scope store
  // unpopulated.
  const navigate = useNavigate({ from: "/feedback" });
  const router = useRouter();
  const search = useSearch({ from: "/feedback" });
  const canonical = canonicalFeedbackPreviewEnabled();
  const openCanonical = (target: FeedbackConversationTarget) => void navigate({search:previous=>({...previous,...feedbackNavigationSearch(target),recap_id:undefined})});
  const closeCanonical = () => void navigate({search:previous=>({...previous,feedback_id:undefined,feedback_player_id:undefined})});
  const recapScope=useRecapScope('recipient'),[recapOverlay,setRecapOverlay]=useState(false),[resourceOverlay,setResourceOverlay]=useState(false);
  const openRecap=(recapId:string)=>void navigate({search:previous=>({...previous,recap_id:recapId,feedback_id:undefined,feedback_player_id:undefined})});
  // Action-first default: bare `/feedback` ⇒ Overview. Legacy
  // `?tab=` values fold to the right action tab.
  const activeTab: FeedbackTab = normalizeFeedbackTab(search.tab);

  const authMe = useAuthStore((s) => s.me);
  const authHydrating = useAuthStore((s) => s.hydrating);
  const canOpenFeedback = getPublicNavCapabilities(authMe).canOpenFeedback;
  const signedInWithoutFeedbackAccess = !!authMe?.user && !canOpenFeedback;
  const feedback = useMyFeedback(!signedInWithoutFeedbackAccess);
  const settingsQuery = useQuery({
    queryKey: publicAppSettingsKey(),
    queryFn: getAppSettings,
    staleTime: 60_000,
  });
  const clubLogoUrl = getUploadedClubLogoUrl(settingsQuery.data);
  // Multi-team users must pick a team before the feedback tabs render.
  const selectionRequired = useScopeStore((s) => s.selectionRequired);
  const activeRole = useScopeStore((s) => s.active?.effective_role);
  const activeTeamName = useScopeStore((s) => s.active?.team.name);
  const isPlayerView = activeRole === "player";
  const selectedPlayerId = selectLinkedPlayer(feedback.data, search);
  const bundle = useMemo(
    () => selectedPlayerId ? feedbackForPlayer(feedback.data, selectedPlayerId) : undefined,
    [feedback.data, selectedPlayerId],
  );
  // #908 P3d — athlete picker + all-seasons view. The chips are the
  // account's linked athletes (not the per-scope roster rows); a chip
  // resolves to that athlete's roster row in the active scope, or opens
  // the athlete-first collection (`?athlete=`), whose view and fences the
  // server decides. Nothing here grants access.
  const athletesQuery = useMeAthletes({ enabled: !signedInWithoutFeedbackAccess });
  const athletes = useMemo<LinkedAthlete[]>(
    () => (Array.isArray(athletesQuery.data) ? athletesQuery.data : []),
    [athletesQuery.data],
  );
  const athleteId = search.athlete;
  const selectedAthleteId =
    athleteId ??
    athletes.find((linked) =>
      (linked.assignments ?? []).some((assignment) => assignment.player_id === selectedPlayerId),
    )?.athlete.id;
  const selectedAthlete = athletes.find((linked) => linked.athlete.id === selectedAthleteId);
  const retiredDestinations = {
    note: undefined, clip: undefined, summary: undefined, goal: undefined,
    feedback_id: undefined, feedback_player_id: undefined, recap_id: undefined,
  } as const;
  // The athlete's roster row in the active scope, if any.
  const scopedPlayerOf = (linked: LinkedAthlete | undefined) =>
    (linked?.assignments ?? []).find((assignment) =>
      feedback.data?.players.some((player) => player.id === assignment.player_id),
    )?.player_id;
  const selectAthlete = (linked: LinkedAthlete) => {
    const scoped = scopedPlayerOf(linked);
    void navigate({
      search: (previous) => ({
        ...previous, ...retiredDestinations,
        // No row in this scope ⇒ clear the previous athlete's `player`, so
        // nothing later falls back to showing that other athlete.
        player: scoped,
        // Stay in the all-seasons mode once it is open; an athlete without
        // a roster row in this scope can only be read all-seasons.
        athlete: athleteId || !scoped ? linked.athlete.id : undefined,
      }),
    });
  };
  // Leaving All seasons needs a roster row of the athlete in this scope; an
  // athlete without one can only be read all-seasons (no toggle-off).
  const canToggleAllSeasons =
    !!selectedAthleteId && (!athleteId || !!scopedPlayerOf(selectedAthlete));
  const toggleAllSeasons = () =>
    void navigate({
      search: (previous) => ({ ...previous, ...retiredDestinations, athlete: athleteId ? undefined : selectedAthleteId }),
    });
  const headingPlayer = bundle?.players.find(
    (player) => player.id === selectedPlayerId,
  );
  const headingName = athleteId ? selectedAthlete?.athlete.display_name : headingPlayer?.display_name;
  const headingSelf = athleteId ? selectedAthlete?.relationship === "self" : isPlayerView;
  const headingFirstName = headingName
    ?.trim()
    .split(/\s+/)[0];
  useEffect(() => {
    if (!authMe?.user || !isRecipientWorkspacePath(router.latestLocation.pathname)) return;
    // The athlete-first view needs no per-scope row; writing the default
    // one would pin another athlete's `player` into the URL.
    if (athleteId) return;
    if (selectedPlayerId && search.player !== selectedPlayerId) {
      void navigate({
        search: (previous) => ({ ...previous, player: selectedPlayerId }),
        replace: true,
      });
    }
  }, [selectedPlayerId, search.player, navigate, authMe?.user, router, athleteId]);

  // The viewer's reviewed-id sets, derived once from `bundle.reviews`
  // — drive the Reviewed/New pills on the Notes + Playlists cards
  // AND the Overview's "what's new" filtering.
  const reviewedNoteIds = useMemo(
    () => deriveReviewedNoteIds(bundle?.reviews ?? []),
    [bundle?.reviews],
  );
  const reviewedPlaylistIds = useMemo(
    () => deriveReviewedPlaylistIds(bundle?.reviews ?? []),
    [bundle?.reviews],
  );
  // PR 7: clips can carry review rows now. Threaded through to the
  // player-facing surfaces so clip cards and clip modals show the right
  // Mark done / Done ✓ affordance.
  const reviewedClipIds = useMemo(
    () => deriveReviewedClipIds(bundle?.reviews ?? []),
    [bundle?.reviews],
  );
  // Phase B: match recaps are markable. Threaded to the Review +
  // History tabs so recap cards show Mark read / Read ✓ and age out
  // of the New band once read.
  const reviewedSummaryIds = useMemo(
    () => deriveReviewedSummaryIds(bundle?.reviews ?? []),
    [bundle?.reviews],
  );

  // M9a A8 — auth gate. A 401 from /api/my-feedback redirects to
  // `/login?next=<current path>` (replaces the M4 <InlineLoginPanel>
  // that used to render inline). Kratos sessions are cookie-backed, so
  // the protected API response is the source of truth rather than the
  // legacy localStorage bearer token.
  const isAuthError =
    feedback.error instanceof ApiError && feedback.error.status === 401;
  const showLogin = isAuthError || (!authHydrating && !authMe?.user);

  // Guard against re-fire after the redirect changes the URL —
  // see AdminShell for the full explanation.
  //
  // `next` captures the FULL path + search (`/feedback?tab=goals`)
  // at mount via a ref so a viewer deep-linked to a specific tab
  // lands back on that tab after signing in.
  const authGateMountPath = useRef(
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}` || "/feedback"
      : "/feedback",
  );
  const authRedirectFired = useRef(false);
  useEffect(() => {
    if (!showLogin) return;
    // Router retains the old shell while another destination loads. Read its
    // latest target here so logout's landing navigation cannot become /login.
    if (!isRecipientWorkspacePath(router.latestLocation.pathname)) return;
    if (authRedirectFired.current) return;
    authRedirectFired.current = true;
    void navigate({
      to: "/login",
      search: { next: authGateMountPath.current },
      replace: true,
    });
  }, [showLogin, navigate, router]);

  const roleRedirectFired = useRef(false);
  useEffect(() => {
    if (showLogin) return;
    if (!signedInWithoutFeedbackAccess) return;
    if (!isRecipientWorkspacePath(router.latestLocation.pathname)) return;
    if (roleRedirectFired.current) return;
    roleRedirectFired.current = true;
    void navigate({ to: "/hub", replace: true });
  }, [showLogin, signedInWithoutFeedbackAccess, navigate, router]);

  if (signedInWithoutFeedbackAccess) {
    return (
      <main
        className="min-h-screen p-4 text-ink sm:p-6"
        data-testid="feedback-shell"
      >
        <p className="font-mono text-sm text-ink-muted">Opening your hub…</p>
      </main>
    );
  }

  return (
    <CanonicalRecapHost scope={canonical?recapScope:null} active={canonical&&!showLogin&&!selectionRequired&&!!selectedPlayerId&&!resourceOverlay}
      matches={bundle?.matches} clubLogoUrl={clubLogoUrl} tenantTeamName={activeTeamName} requestedRecapId={canonical&&selectedPlayerId?search.recap_id:undefined}
      onCloseDestination={()=>void navigate({search:previous=>({...previous,recap_id:undefined})})} onOverlayChange={setRecapOverlay}>
    <main
      className="min-h-screen p-4 text-ink sm:p-6"
      data-testid="feedback-shell"
    >
      <header className="family-header family-header-compact">
        <div className="family-title-row">
          <h1 className="flex items-center gap-1.5 text-2xl font-display tracking-display sm:text-3xl">
            {headingSelf
              ? "My feedback."
              : headingFirstName
              ? `${headingFirstName}’s feedback.`
              : "My Feedback"}
            <HelpLink />
          </h1>
          <GlobalScopeBar />
        </div>
        {!showLogin && !selectionRequired ? (
          <section
            className="family-player-context"
            aria-label={isPlayerView ? "Your player profile" : "Viewing player"}
            data-player-layout={
              (athletes.length || feedback.data?.players.length || 0) === 1
                ? "single"
                : "multiple"
            }
          >
            <p className="family-player-context-label">
              {isPlayerView ? "Player" : "Viewing"}
            </p>
            <LinkedPlayersStrip
              key={selectedPlayerId ?? "choose-player"}
              players={feedback.data?.players ?? []}
              photoRequests={feedback.data?.player_photo_requests ?? []}
              selectedPlayerId={selectedPlayerId}
              athletes={athletes}
              selectedAthleteId={selectedAthleteId}
              onSelectAthlete={isPlayerView && athletes.length <= 1 ? undefined : selectAthlete}
              allSeasons={!!athleteId}
              onToggleAllSeasons={canToggleAllSeasons ? toggleAllSeasons : undefined}
              onSelect={
                isPlayerView && !!selectedPlayerId && (feedback.data?.players.length ?? 0) <= 1
                  ? undefined
                  : (player) =>
                      void navigate({
                        search: (previous) => ({
                          ...previous,
                          player,
                          note: undefined,
                          clip: undefined,
                          summary: undefined,
                          goal: undefined,
                          feedback_id: undefined,
                          feedback_player_id: undefined,
                          recap_id: undefined,
                        }),
                      })
              }
            />
          </section>
        ) : null}
      </header>

      {showLogin ? null : athleteId ? (
        /* #908 P3d: the athlete-first collection replaces the per-scope
         * tabs while `?athlete=` is set; the picker's All seasons control
         * toggles back. It is checked BEFORE the team gate: the
         * collection endpoint needs no scope, so a linked recipient on
         * several teams with no saved scope still reads the athlete. */
        <CanonicalAthleteFeedback
          key={athleteId}
          athleteId={athleteId}
          view="recipient"
          onOpen={canonical ? openCanonical : undefined}
          onOpenRecap={canonical ? openRecap : undefined}
          matches={bundle?.matches}
          clubLogoUrl={clubLogoUrl}
          tenantTeamName={activeTeamName}
          onOverlayChange={setResourceOverlay}
        />
      ) : selectionRequired ? (
        <TeamScopeGate recipient />
      ) : !selectedPlayerId ? (
        feedback.isError ? <FeedbackErrorState onRetry={() => void feedback.refetch()} />
          : feedback.isLoading ? <FeedbackLoadingState />
          : <p role="status" className="text-sm text-ink-muted">{(feedback.data?.players.length ?? 0) > 0
            ? "Choose a linked player to view Feedback in this team and season."
            : "No linked player is available in this team. Choose another team or ask your coach about your link."}</p>
      ) : (
        <>
          {/* Linked-players chip strip — who's linked to this
           * account (empty ⇒ "ask a coach"). */}

          {/* The sub-tab strip leads the tab content. */}
          <div className="mb-4">
            <FeedbackTabStrip active={activeTab} playerId={selectedPlayerId} />
          </div>

          <section
            key={selectedPlayerId ?? "no-player"}
            aria-label={FEEDBACK_TAB_LABELS[activeTab]}
            data-testid={`feedback-panel-${activeTab}`}
          >
            {feedback.isError && !isAuthError && !canonical ? (
              <FeedbackErrorState onRetry={() => void feedback.refetch()} />
            ) : feedback.isLoading && !canonical ? (
              <FeedbackLoadingState />
            ) : activeTab === "overview" ? (
              <FeedbackOverviewTab
                onOpenFeedback={canonical ? openCanonical : undefined}
                onOpenRecap={canonical ? openRecap : undefined}
                onOverlayChange={setResourceOverlay}
                clubLogoUrl={clubLogoUrl} tenantTeamName={activeTeamName}
                bundle={bundle}
                reviewedNoteIds={reviewedNoteIds}
                reviewedClipIds={reviewedClipIds}
              />
            ) : canonical && (activeTab === "review" || activeTab === "history") ? (
              <>{(search.summary!=null||search.note!=null||search.clip!=null)&&<p role="status" className="mb-4 rounded-control border border-rule p-3 text-sm">This link refers to an older Feedback item. Select current Feedback or a recap below; the old identifier has not been converted.</p>}
              <CanonicalRecipientFeedback key={selectedPlayerId} playerId={selectedPlayerId} onOpen={openCanonical} onOpenRecap={openRecap}
                matches={bundle?.matches} clubLogoUrl={clubLogoUrl} tenantTeamName={activeTeamName} onOverlayChange={setResourceOverlay}/></>
            ) : activeTab === "review" ? (
              <FeedbackReviewTab
                bundle={bundle}
                clubLogoUrl={clubLogoUrl}
                tenantTeamName={activeTeamName}
                reviewedNoteIds={reviewedNoteIds}
                reviewedPlaylistIds={reviewedPlaylistIds}
                /* PR 7: clip review state — drives the Mark done /
                 * Done ✓ affordance on clip cards. Empty for
                 * pre-PR 7 bundles, which keeps clips `new`. */
                reviewedClipIds={reviewedClipIds}
                /* Phase B: recap read state — drives Mark read /
                 * Read ✓ on summary cards. */
                reviewedSummaryIds={reviewedSummaryIds}
                /* PR 2: `?summary=<id>` deep-links to a specific
                 * recap. The Overview's "Read full recap →" /
                 * "Read summary" actions pass this so the Review
                 * tab auto-applies the Summaries filter + expands
                 * the matching card on mount. */
                initialSummaryId={search.summary ?? null}
                /* Phase 2.2: the bell now links to the ITEM, not just
                 * the tab, so a player who taps a notification lands
                 * on the thing that notified them instead of hunting
                 * for it in the feed. Applied once per distinct value
                 * (see `useDeepLinkTarget`) so a background refetch
                 * can't yank a reader back to it later. */
                deepLinkNoteId={search.note ?? null}
                deepLinkClipId={search.clip ?? null}
              />
            ) : activeTab === "goals" ? (
              <FeedbackGoalsTab
                onOpenFeedback={canonical ? openCanonical : undefined}
                onOverlayChange={setResourceOverlay}
                bundle={bundle}
                reviewedNoteIds={reviewedNoteIds}
                reviewedPlaylistIds={reviewedPlaylistIds}
                reviewedClipIds={reviewedClipIds}
              />
            ) : (
              <FeedbackHistoryTab
                bundle={bundle}
                clubLogoUrl={clubLogoUrl}
                tenantTeamName={activeTeamName}
                reviewedNoteIds={reviewedNoteIds}
                reviewedClipIds={reviewedClipIds}
                reviewedSummaryIds={reviewedSummaryIds}
              />
            )}
          </section>
        </>
      )}
      {canonical && !!selectedPlayerId && !showLogin && !selectionRequired && <FeedbackConversationHost view="recipient" target={feedbackNavigationTarget(search)} players={feedback.error ? [] : feedback.data?.players} invalid={!feedbackNavigationTarget(search) && !!(search.feedback_id || search.feedback_player_id)} onClose={closeCanonical} suspended={recapOverlay||resourceOverlay||!!search.recap_id} />}
    </main>
    </CanonicalRecapHost>
  );
}

function FeedbackLoadingState() {
  return (
    <div
      className="family-feedback-state family-feedback-loading"
      role="status"
      aria-label="Loading"
      data-testid="feedback-loading"
    >
      <p className="eyebrow">Loading your team…</p>
      <span className="family-feedback-skeleton family-feedback-skeleton-wide" />
      <span className="family-feedback-skeleton family-feedback-skeleton-short" />
      <span className="family-feedback-skeleton family-feedback-skeleton-panel" />
    </div>
  );
}

function FeedbackErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      className="family-feedback-state family-feedback-error"
      data-testid="feedback-bundle-error"
    >
      <span className="family-feedback-state-icon" aria-hidden="true">
        <Inbox size={26} strokeWidth={1.8} />
      </span>
      <h2>We couldn’t load this right now.</h2>
      <p>Your work is still here. Try loading this view again.</p>
      <Button type="button" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}
